/**
 * Cuentas bancarias (IBAN).
 *
 * Vive en `shared` por lo mismo que el DNI: el formulario tiene que avisar mientras se teclea
 * y el servidor rechazar exactamente lo mismo, y el lector del contrato tiene que dar por buena
 * la misma cuenta que después va a aceptar el alta.
 */

/** «es78 2095-0553 50 9124930394» → «ES7820950553509124930394»: como se guarda. */
export function normalizeIban(value: string): string {
  return value.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Si pasa su dígito de control (ISO 13616, módulo 97). Los españoles tienen 24 caracteres:
 * uno de 23 o de 25 pasaría el módulo por casualidad y sería una cuenta que no existe.
 */
export function isValidIban(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith('ES') && iban.length !== 24) return false;
  let resto = 0;
  for (const caracter of `${iban.slice(4)}${iban.slice(0, 4)}`) {
    const valor = /\d/.test(caracter) ? caracter : String(caracter.charCodeAt(0) - 55);
    for (const cifra of valor) resto = (resto * 10 + Number(cifra)) % 97;
  }
  return resto === 1;
}

/** «ES7820950553509124930394» → «ES78 2095 0553 5091 2493 0394», como se lee en papel. */
export function formatIban(value: string): string {
  return normalizeIban(value).replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * Una cuenta española de 20 cifras (el CCC de antes del IBAN) con su «ES» y sus dígitos de
 * control delante: «2100 0418 45 0200051332» → «ES9121000418450200051332».
 */
export function ibanFromCcc(ccc: string): string {
  const digitos = ccc.replace(/\D/g, '');
  let resto = 0;
  // «ES00» al final, con E = 14 y S = 28: lo que exige el cálculo del módulo 97.
  for (const cifra of `${digitos}142800`) resto = (resto * 10 + Number(cifra)) % 97;
  return `ES${String(98 - resto).padStart(2, '0')}${digitos}`;
}

/**
 * El error de una cuenta, o `null` si es válida. Solo se juzga una cuenta ENTERA: a quien lleva
 * medio IBAN escrito no se le dice que está mal, se le deja terminar.
 */
export function ibanError(value: string): string | null {
  const iban = normalizeIban(value);
  if (!iban) return null;
  if (!/^[A-Z]{2}\d{2}/.test(iban)) return 'Empieza por el país y dos cifras: «ES78…».';
  if (iban.startsWith('ES') && iban.length !== 24) return 'Una cuenta española tiene 24 caracteres.';
  return isValidIban(iban) ? null : 'La cuenta no cuadra con sus dígitos de control: revísala.';
}
